import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import api from "../axios";
import { Building, AlertCircle, Loader2, CheckCircle, XCircle, Users } from "lucide-react";
import { msalInstance } from "../index";
import { API_URL } from "../env";
import { forceLogout } from "../utils/authSession";
import InvitationService from "../services/invitationService";


// ─── Invitation Selection Modal ─────────────────────────────────────────────────
// Shown when a user without an organization has pending invitations.
const InvitationSelectionModal = ({ open, invitations, onAccept, onRejectAll }) => {
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingAll, setRejectingAll] = useState(false);
  const [error, setError] = useState(null);

  if (!open || !invitations || invitations.length === 0) return null;

  const handleAccept = async (invitation) => {
    setAcceptingId(invitation.id);
    setError(null);
    try {
      await onAccept(invitation);
    } catch (e) {
      const errorMessage = e?.response?.data?.detail || e?.message || "Failed to accept invitation";
      setError(errorMessage);
      setAcceptingId(null);
    }
  };

  const handleRejectAll = async () => {
    setRejectingAll(true);
    setError(null);
    try {
      await onRejectAll();
    } catch (e) {
      const errorMessage = e?.response?.data?.detail || e?.message || "Failed to reject invitations";
      setError(errorMessage);
      setRejectingAll(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4">
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg pointer-events-auto border border-slate-200/60 overflow-hidden">
        {/* Header */}
        <div className="px-8 py-8 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-accent-600 rounded-2xl shadow-lg shadow-accent-600/20">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">You've Been Invited!</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">
                Choose an organization to join
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
          {/* Info */}
          <p className="text-sm text-slate-600 font-medium">
            You have been invited to join the following organization{invitations.length > 1 ? 's' : ''}. 
            Select one to continue.
          </p>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 font-bold">{error}</p>
            </div>
          )}

          {/* Invitation List */}
          <div className="space-y-3">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl hover:border-accent-300 hover:bg-accent-050 transition-all group"
              >
                {/* Left: Org Info */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center font-black text-sm flex-shrink-0 border border-accent-200/50">
                    {(invitation.organization_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">
                      {invitation.organization_name}
                    </p>
                    <p className="text-xs text-slate-500 font-medium truncate">
                      Invited by {invitation.created_by} · {invitation.invited_role}
                    </p>
                  </div>
                </div>

                {/* Right: Accept Button */}
                <button
                  onClick={() => handleAccept(invitation)}
                  disabled={acceptingId !== null || rejectingAll}
                  className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-xs uppercase tracking-wide shadow-md shadow-slate-900/10 active:scale-[0.98] disabled:opacity-50 flex-shrink-0 ml-4"
                >
                  {acceptingId === invitation.id ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Joining...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Accept</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer — Reject All */}
        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400 font-medium max-w-[60%]">
              Don't want to join any of these? Reject all and create your own organization.
            </p>
            <button
              onClick={handleRejectAll}
              disabled={acceptingId !== null || rejectingAll}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-red-200 text-red-600 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all font-bold text-xs uppercase tracking-wide active:scale-[0.98] disabled:opacity-50"
            >
              {rejectingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Rejecting...</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4" />
                  <span>Reject All</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};


// ─── Setup Organization Modal ───────────────────────────────────────────────────
// blocking modal component
const SetupOrganizationModal = ({ open, onSubmit }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await onSubmit({ name, address });
    } catch (e) {
      const errorMessage = e?.response?.data?.detail || e?.response?.data?.message || e?.message || "Failed to setup organization";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[10000] p-4"
    >
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md pointer-events-auto border border-slate-200/60 overflow-hidden">
        {/* Header */}
        <div className="px-8 py-8 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-accent-600 rounded-2xl shadow-lg shadow-accent-600/20">
              <Building className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">Setup Organization</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Create your organizational profile</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-8 py-8">
          <div className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label htmlFor="org-name" className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter organization name"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-100 focus:border-accent-500 focus:bg-white transition-all text-sm font-bold text-slate-900 shadow-sm shadow-slate-200/50 placeholder-slate-300"
                disabled={loading}
                required
              />
            </div>

            {/* Address Field */}
            <div className="space-y-2">
              <label htmlFor="org-address" className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                id="org-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter organization address"
                className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-accent-100 focus:border-accent-500 focus:bg-white transition-all text-sm font-bold text-slate-900 shadow-sm shadow-slate-200/50 placeholder-slate-300"
                disabled={loading}
                required
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 font-bold">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end mt-8 pt-6 border-t border-slate-100">
            <button
              type="submit"
              disabled={loading || !name.trim() || !address.trim()}
              className="px-8 py-3 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all font-black text-xs uppercase tracking-[0.15em] shadow-xl shadow-slate-900/10 active:scale-[0.98] disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Organization</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};


// ─── Main Hook ──────────────────────────────────────────────────────────────────
const useOrganizationId = (user) => {
  const initialId =
    user?.organization_id || localStorage.getItem("organization_id") || null;
  const [organizationId, setOrganizationId] = useState(initialId);
  const [organization, setOrganization] = useState(null);
  const [orgLoading, setOrgLoading] = useState(!initialId);
  const [orgError, setOrgError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Invitation state
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const handleSessionInvalidation = useCallback(() => {
    forceLogout(msalInstance);
  }, []);

  const fetchDefaultOrganization = useCallback(async () => {
    setOrgLoading(true);
    try {
      const response = await api.get(`${API_URL}/users/me`);
      const resolvedId = response.data?.client_org_id || response.data?.organization_id;

      // Check if user exists (response.data should have user info)
      if (!response.data || !response.data.id) {
        throw new Error("User profile could not be loaded");
      }

      if (!resolvedId) {
        // ──────────────────────────────────────────────────────────────────
        // NEW: Before showing the Setup Org modal, check for incoming invitations
        // ──────────────────────────────────────────────────────────────────
        try {
          const invResponse = await InvitationService.getIncoming();
          const incoming = Array.isArray(invResponse.data) ? invResponse.data : [];
          const pending = incoming.filter(inv => inv.status === 'pending');

          if (pending.length > 0) {
            // User has pending invitations — show the invitation selection modal
            setPendingInvitations(pending);
            setInvitationModalOpen(true);
            setOrgLoading(false);
            return; // Don't show Setup Org modal yet
          }
        } catch (invError) {
          console.error("Failed to check incoming invitations:", invError);
          // Fall through to showing Setup Org modal
        }

        setModalOpen(true); // show modal if missing (no invitations found)
        throw new Error("No organization id available");
      }

      // Verify organization exists
      try {
        await api.get(`${API_URL}/organizations/${resolvedId}`);
        setOrganizationId(String(resolvedId));
        localStorage.setItem("organization_id", resolvedId);
        setOrgError(null);
      } catch (orgError) {
        // Organization doesn't exist (404) or access denied (401/403)
        const status = orgError.response?.status;
        if (status === 401) {
          handleSessionInvalidation();
          return;
        }
        throw orgError;
      }
    } catch (error) {
      const status = error.response?.status;
      // If unauthorized (401), redirect to login
      if (status === 401) {
        handleSessionInvalidation();
        return;
      }
      console.error("Failed to resolve organization id:", error);
      setOrgError(error);
    } finally {
      setOrgLoading(false);
    }
  }, [handleSessionInvalidation]);

  useEffect(() => {
    if (!organizationId) {
      fetchDefaultOrganization();
    } else {
      // Even if organizationId exists, validate it periodically
      const validateOrganization = async () => {
        try {
          // Only perform the "heartbeat" call if the window is currently focused
          if (!document.hasFocus()) {
            return;
          }
          const response = await api.get(`${API_URL}/organizations/${organizationId}`);
          setOrganization(response.data);
        } catch (error) {
          const status = error.response?.status;
          if (status === 401) {
            handleSessionInvalidation();
          }
        }
      };

      // Validate every 60 seconds
      const POLLING_INTERVAL = 60000;
      const interval = setInterval(validateOrganization, POLLING_INTERVAL);

      // Initial fetch to get details if we only have ID
      if (!organization) {
        validateOrganization();
      }

      return () => clearInterval(interval);
    }
  }, [organizationId, organization, fetchDefaultOrganization, handleSessionInvalidation]);

  // ──────────────────────────────────────────────────────────────────────────────
  // Handle accepting an invitation
  // ──────────────────────────────────────────────────────────────────────────────
  const handleAcceptInvitation = async (invitation) => {
    // Accept the invitation via API
    await InvitationService.accept(invitation.id);

    // Re-fetch the user to get the updated client_org_id
    const userResponse = await api.get(`${API_URL}/users/me`);
    const newOrgId = userResponse.data?.client_org_id || userResponse.data?.organization_id;

    if (!newOrgId) {
      throw new Error("Organization ID not set after accepting invitation");
    }

    // Update local state
    setOrganizationId(String(newOrgId));
    localStorage.setItem("organization_id", newOrgId);
    setOrgError(null);
    setInvitationModalOpen(false);
    setPendingInvitations([]);

    // Fetch org details
    try {
      const orgResponse = await api.get(`${API_URL}/organizations/${newOrgId}`);
      setOrganization(orgResponse.data);
    } catch (e) {
      console.error("Failed to fetch organization details after accepting:", e);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────────
  // Handle rejecting all invitations → show Setup Org modal
  // ──────────────────────────────────────────────────────────────────────────────
  const handleRejectAllInvitations = async () => {
    await InvitationService.rejectAll();

    // Close invitation modal, open Setup Org modal
    setInvitationModalOpen(false);
    setPendingInvitations([]);
    setModalOpen(true);
  };

  const handleModalSubmit = async ({ name, address }) => {
    try {
      // Get user email from user object
      const userEmail = user?.email || user?.username || '';
      if (!userEmail) {
        throw new Error("User email not found");
      }

      // Step 1: Create organization with required fields
      const orgData = {
        name: name.trim(),
        address: address.trim(),
        status: "ACTIVE",
        type: "third-party",
        created_by: userEmail
      };

      const orgResponse = await api.post(`${API_URL}/organizations`, orgData);
      const organizationId = orgResponse.data?.id;

      if (!organizationId) {
        throw new Error("Failed to create organization: No organization ID returned");
      }

      // Step 2: Get user id from /users/me
      const userResponse = await api.get(`${API_URL}/users/me`);
      const userId = userResponse.data?.id;

      if (!userId) {
        throw new Error("Failed to get user ID");
      }



      // Step 3: Update user with client_org_id using PUT
      await api.put(`${API_URL}/users/${userId}`, {
        client_org_id: organizationId
      });

      // Update local state and storage first
      setOrganizationId(String(organizationId));
      setOrganization(orgResponse.data);
      localStorage.setItem("organization_id", organizationId);
      setOrgLoading(false);
      setOrgError(null);
      // Close modal after state is updated
      setModalOpen(false);
    } catch (error) {
      // Re-throw error to be caught in modal
      throw error;
    }
  };

  return {
    organization,
    organizationId,
    orgLoading,
    orgError,
    modalOpen,
    handleModalSubmit,
    SetupOrganizationModal,
    // New exports for invitation flow
    InvitationSelectionModal: (props) => (
      <InvitationSelectionModal
        {...props}
        open={invitationModalOpen}
        invitations={pendingInvitations}
        onAccept={handleAcceptInvitation}
        onRejectAll={handleRejectAllInvitations}
      />
    ),
    invitationModalOpen,
  };
};

export default useOrganizationId;
